import { formatValue } from "../../../domains/formatting";

// Share of `sum` as a whole percent. Positive shares below half a percent
// floor to "<1%" instead of rounding to a misleading "0%"; negative values
// (emission corrections are not sign-guarded upstream) show their real
// negative share. Null when the sum is not positive: no meaningful share
// exists, so callers drop the percentage rather than divide by zero.
export const formatSharePercent = (value, sum) => {
  if (!Number.isFinite(value) || !Number.isFinite(sum) || sum <= 0) {
    return null;
  }
  const share = (value / sum) * 100;
  if (share > 0 && share < 0.5) return "<1%";
  // Symmetric floor for tiny negative shares (corrections): ">-1%" keeps the
  // sign visible instead of rounding to a false "0%".
  if (share < 0 && share > -0.5) return ">-1%";
  // Round the magnitude so negative shares mirror positive ones exactly
  // (Math.round alone sends -0.5 to 0 but 0.5 to 1).
  const rounded = Math.sign(share) * Math.round(Math.abs(share));
  return `${Object.is(rounded, -0) ? 0 : rounded}%`;
};

// Null/undefined/empty stay null (Number(null) would coerce to 0 and rank a
// no-data entity alongside genuine zeros); anything non-finite is also null.
const numericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

// One sorted row per recharts payload entry, biggest value first, ties (and
// non-numeric values, which sort last) keeping bar order. Each row carries
// "value (share%)" where the share denominator is the sum of the entries in
// the payload, i.e. the entities currently displayed for that year.
export const buildSortedTooltipRows = (payload, nameFor) => {
  const entries = (payload ?? []).map((item, idx) => ({
    item,
    idx,
    num: numericValue(item?.value),
  }));
  const sum = entries.reduce((acc, entry) => acc + (entry.num ?? 0), 0);
  const rows = [...entries]
    .sort(
      (a, b) =>
        (b.num ?? Number.NEGATIVE_INFINITY) -
          (a.num ?? Number.NEGATIVE_INFINITY) || a.idx - b.idx,
    )
    .map(({ item, num, idx }) => {
      const percent = num === null ? null : formatSharePercent(num, sum);
      const value = num === null ? formatValue(Number.NaN) : formatValue(num);
      return {
        key: String(item?.dataKey ?? item?.name ?? idx),
        name: nameFor ? nameFor(item) : String(item?.name ?? ""),
        color: item?.color || item?.fill || undefined,
        valueLabel: percent ? `${value} (${percent})` : value,
      };
    });
  return { rows, sum };
};

// Newest year that has data to rank by: a boundary can extend years past the
// last submission (e.g. to 2036), so the newest selected year alone can be
// all-null and would make the ranking silently fall back to picker order.
export const pickRankingYear = (years, hasDataForYear) =>
  (years ?? [])
    .slice()
    .sort((a, b) => b - a)
    .find((year) => hasDataForYear(year));

// Legend order for the per-entity charts: latest-year total descending,
// entities without a total for that year last, ties keeping picker order.
// Bar order and colors stay untouched; only the legend tells the ranking.
export const sortEntitiesByTotalDesc = (entities, getTotal) =>
  entities
    .map((id, idx) => ({ id, idx, total: numericValue(getTotal(id)) }))
    .sort(
      (a, b) =>
        (b.total ?? Number.NEGATIVE_INFINITY) -
          (a.total ?? Number.NEGATIVE_INFINITY) || a.idx - b.idx,
    )
    .map((entry) => entry.id);
